import { Component, OnInit, EventEmitter, Output, ChangeDetectorRef, AfterViewInit, DestroyRef, inject, HostListener } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { MENU, BOTTOM_MENU } from './menu';
import { SidebarMenu } from "../../core/interfaces/sidebar-menu";
import { User } from "../../core/interfaces/user";
import { UserService } from "../../core/services/user.service";
import { PermissionService } from "../../core/services/permission.service";
import { BookingNotificationService } from '../../core/services/booking-notification.service';
import { Hotel } from '../../core/interfaces/hotel';
import type { Observable } from 'rxjs';
import { HotelService } from '../../core/services/hotel.service';
import { LanguageService } from '../../core/services/language.service';
import { DialogService } from '../../core/services/dialog.service';
import { LoggerService } from '../../core/services/logger.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    standalone: false
})

/**
 * Sidebar Component
 */
export class SidebarComponent implements OnInit, AfterViewInit {


  menuItems: SidebarMenu[] = [];
  bottomMenuItems: SidebarMenu[] = [];
  @Output() mobileMenuButtonClicked = new EventEmitter();
  user: User = new User();
  pendingCount = 0;
  managedHotel: Hotel | null = null;
  hotelId = 0;
  public hotel$: Observable<Hotel | null>;
  private destroyRef = inject(DestroyRef);

  // Language settings
  listLang = [
    { name: 'Deutsch', flag: 'assets/images/flags/germany.svg', lang: 'de' },
    { name: 'Français', flag: 'assets/images/flags/france.svg', lang: 'fr' },
    { name: 'Italiano', flag: 'assets/images/flags/italy.svg', lang: 'it' },
    { name: 'English', flag: 'assets/images/flags/united-kingdom.svg', lang: 'en' },
  ];

  constructor(
    private router: Router,
    public translate: TranslateService,
    private userService: UserService,
    private hotelService: HotelService,
    private permissionService: PermissionService,
    private bookingNotificationService: BookingNotificationService,
    private cd: ChangeDetectorRef,
    public languageService: LanguageService,
    private dialogService: DialogService,
    private logger: LoggerService,
    private snackBar: MatSnackBar,
  ) {
    this.hotel$ = this.hotelService.selectedHotel$;
    translate.setDefaultLang('en');

    // Single subscription to router events with takeUntilDestroyed
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this._activateMenuDropdown();
      this.cd.markForCheck();
      // Close mobile sidebar on navigation
      this.closeMobileSidebar();
    });
  }

  ngOnInit(): void {
    this.menuItems = MENU;
    this.bottomMenuItems = BOTTOM_MENU;
    this.user = this.userService.getUser();

    // Restore sidebar state from localStorage
    this.restoreSidebarState();

    // Restore last hotel ID from localStorage for pages without hotel context (e.g., profile)
    const storedHotelId = localStorage.getItem('lastHotelId');
    if (storedHotelId) {
      this.hotelId = parseInt(storedHotelId, 10);
    }

    this.bookingNotificationService.pendingCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => {
        this.pendingCount = count;
      });
    this.bookingNotificationService.fetchPendingCountFromAllPages();

    this.hotelService.selectedHotel$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hotel: Hotel | null) => {
        if (hotel) {
          this.hotelId = hotel.id;
          this.managedHotel = hotel;
          // Persist hotel ID for pages without hotel context
          localStorage.setItem('lastHotelId', hotel.id.toString());
        }
      });
  }

  /**
   * Toggle the menu bar when having mobile screen
   */
  toggleMobileMenu(event: Event): void {
    const currentSidebarSize = document.body.getAttribute('data-sidebar-size');

    if (window.innerWidth >= 992) {
      // Keep the mobile overlay class off desktop: it places the topbar above
      // the sidebar and can clip the wordmark while toggling the navigation.
      document.body.classList.remove('sidebar-enable');

      let newSize: string;
      if (currentSidebarSize == null || currentSidebarSize === 'lg') {
        newSize = 'sm';
      } else if (currentSidebarSize === 'md') {
        newSize = 'sm';
      } else {
        newSize = 'lg';
      }
      document.body.setAttribute('data-sidebar-size', newSize);
      // Persist sidebar state
      localStorage.setItem('sidebarSize', newSize);
    } else {
      document.body.classList.toggle('sidebar-enable');
    }
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  /**
   * Restore sidebar collapsed state from localStorage
   */
  private restoreSidebarState(): void {
    if (window.innerWidth >= 992) {
      const savedSize = localStorage.getItem('sidebarSize');
      if (savedSize) {
        document.body.setAttribute('data-sidebar-size', savedSize);
      }
    } else {
      // On mobile, always show expanded sidebar
      document.body.setAttribute('data-sidebar-size', 'lg');
    }
  }

  /**
   * Handle window resize to reset sidebar state between mobile and desktop
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth < 992) {
      // Mobile: always show expanded sidebar menu
      document.body.setAttribute('data-sidebar-size', 'lg');
      // Close mobile sidebar overlay on resize to desktop
    } else {
      document.body.classList.remove('sidebar-enable');
      // Desktop: restore saved preference
      const savedSize = localStorage.getItem('sidebarSize');
      if (savedSize) {
        document.body.setAttribute('data-sidebar-size', savedSize);
      }
    }
  }

  /***
   * Activate droup down set
   */
  ngAfterViewInit(): void {
    this._initMenuToggle();
    this._activateMenuDropdown();
  }

  /**
   * Replaces MetisMenu: adds click handlers to parent links
   * that toggle the `.mm-show` class on sibling sub-menus.
   */
  private _initMenuToggle(): void {
    const sideMenu = document.getElementById('side-menu');
    if (!sideMenu) return;

    sideMenu.addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;
      const parentLink = target.closest('a.is-parent, a.has-arrow');
      if (!parentLink) return;

      event.preventDefault();
      const subMenu = parentLink.nextElementSibling;
      if (!subMenu || !subMenu.classList.contains('sub-menu')) return;

      // Close other open submenus at the same level
      const siblings = parentLink.parentElement?.parentElement?.querySelectorAll(':scope > li > ul.sub-menu.mm-show');
      siblings?.forEach(sibling => {
        if (sibling !== subMenu) {
          sibling.classList.remove('mm-show');
          sibling.parentElement?.classList.remove('mm-active');
        }
      });

      // Toggle the clicked submenu
      subMenu.classList.toggle('mm-show');
      parentLink.parentElement?.classList.toggle('mm-active');
    });
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: SidebarMenu): boolean {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className: string): void {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Activate the parent dropdown
   * Finds the current menu item based on URL and activates its parent dropdowns
   */
  _activateMenuDropdown(): void {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');

    const menuItemEl = this._findCurrentMenuItem();
    if (!menuItemEl) {
      return;
    }

    menuItemEl.classList.add('active');
    this._activateParentElements(menuItemEl);
  }

  /**
   * Find the sidebar link element that matches the current URL.
   *
   * Four fallback strategies (in order):
   * 1. Exact URL match         — /123/rooms          → rooms link
   * 2. Parent path match       — /123/hotel-managers/create → hotel-managers link
   * 3. Grandparent path match  — /123/hotel-managers/edit/5 → hotel-managers link
   * 4. activeRoutes match      — /123/add-room        → rooms link (sibling routes)
   */
  private _findCurrentMenuItem(): Element | null {
    const links = document.getElementsByClassName('side-nav-link-ref');
    const paths: string[] = [];

    for (const link of Array.from(links)) {
      paths.push((link as HTMLAnchorElement).pathname);
    }

    const currentPath = window.location.pathname;

    // 1. Exact match
    const exactIndex = paths.indexOf(currentPath);
    if (exactIndex !== -1) {
      return links[exactIndex];
    }

    // 2. Parent path match — strips the last URL segment
    const parentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
    const parentIndex = paths.indexOf(parentPath);
    if (parentIndex !== -1) {
      return links[parentIndex];
    }

    // 3. Grandparent path match — strips two URL segments (e.g. /hotel-managers/edit/:id)
    const grandparentPath = parentPath.substring(0, parentPath.lastIndexOf('/'));
    const grandparentIndex = paths.indexOf(grandparentPath);
    if (grandparentIndex !== -1) {
      return links[grandparentIndex];
    }

    // 4. activeRoutes match — for sibling routes declared on a menu item
    //    Hotel-scoped URLs are /{hotelId}/{route}/... so the route segment is at index 1
    const urlSegments = currentPath.split('/').filter(Boolean);
    const currentRouteSegment = urlSegments.length >= 2 ? urlSegments[1] : (urlSegments[0] ?? '');

    const matchedItem = this.menuItems.find(item =>
      item.activeRoutes?.some((route: string) => currentRouteSegment === route)
    );

    if (matchedItem?.link) {
      for (const link of Array.from(links)) {
        const linkEl = link as HTMLAnchorElement;
        if (linkEl.pathname.endsWith(`/${matchedItem.link}`)) {
          return linkEl;
        }
      }
    }

    return null;
  }

  /**
   * Activate parent elements of the current menu item
   * Walks up the DOM tree activating dropdown menus
   */
  private _activateParentElements(menuItemEl: Element): void {
    const parentEl = menuItemEl.parentElement;
    if (!parentEl) {
      return;
    }

    parentEl.classList.add('mm-active');

    const parent2El = parentEl.parentElement?.closest('ul');
    if (!parent2El || parent2El.id === 'side-menu') {
      return;
    }

    parent2El.classList.add('mm-show');
    this._activateDeepParentElements(parent2El);
  }

  /**
   * Activate deeply nested parent elements (level 3 and beyond)
   */
  private _activateDeepParentElements(parent2El: Element): void {
    const parent3El = parent2El.parentElement;
    if (!parent3El || parent3El.id === 'side-menu') {
      return;
    }

    parent3El.classList.add('mm-active');
    this._activateChildAnchors(parent3El);

    const parent4El = parent3El.parentElement;
    if (!parent4El || parent4El.id === 'side-menu') {
      return;
    }

    parent4El.classList.add('mm-show');

    const parent5El = parent4El.parentElement;
    if (!parent5El || parent5El.id === 'side-menu') {
      return;
    }

    parent5El.classList.add('mm-active');
    const isParentAnchor = parent5El.querySelector('.is-parent');
    if (isParentAnchor) {
      isParentAnchor.classList.add('mm-active');
    }
  }

  /**
   * Activate child anchor elements within a parent
   */
  private _activateChildAnchors(parentEl: Element): void {
    const childAnchor = parentEl.querySelector('.has-arrow');
    const childDropdown = parentEl.querySelector('.has-dropdown');

    if (childAnchor) {
      childAnchor.classList.add('mm-active');
    }
    if (childDropdown) {
      childDropdown.classList.add('mm-active');
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Check if user has access to a menu item based on access areas.
   *
   * Logic:
   * - superAdminOnly items: only Super Admins see them
   * - internalOnly items: only internal staff see them
   * - items with accessArea: internal users always see; hotel users need the area
   * - items with no access restriction: visible to everyone
   */
  hasMenuAccess(item: SidebarMenu): boolean {
    // Super Admin only (e.g., Extranet Users)
    if (item.superAdminOnly) {
      return this.permissionService.isSuperAdmin();
    }

    // Internal only (e.g., Hotel Settings, SEO, Documents, Concierge KB)
    if (item.internalOnly) {
      return this.permissionService.isInternal();
    }

    // Label exclusion — hide item when hotel has the specified label (e.g., hide Gift Vouchers for no_eguma hotels)
    if (item.hideForLabel && this.managedHotel?.labels) {
      const hasLabel = this.managedHotel.labels.some(label => label.code === item.hideForLabel);
      if (hasLabel) {
        return false;
      }
    }

    // Hotel setting required (e.g., yielding tools only visible when yield_price or last_minute_yielding is enabled)
    if (item.requiresHotelSetting && this.managedHotel) {
      const settings = Array.isArray(item.requiresHotelSetting) ? item.requiresHotelSetting : [item.requiresHotelSetting];
      const hotelData = this.managedHotel as unknown as Record<string, unknown>;
      const anyEnabled = settings.some(s => !!hotelData[s]);
      if (!anyEnabled) {
        return false;
      }
    }

    // Access area required (e.g., bookings, hotel-profile)
    if (item.accessArea) {
      // Internal users see all hotel pages
      if (this.permissionService.isInternal()) {
        return true;
      }
      return this.permissionService.hasAccessArea(item.accessArea);
    }

    // No restriction — visible to everyone
    return true;
  }

  /**
   * Check if a divider should appear before this section title.
   * Dividers appear before every visible section except the first one.
   */
  showDividerBefore(titleIndex: number): boolean {
    for (let i = 0; i < titleIndex; i++) {
      if (this.menuItems[i].isTitle && this.isSectionVisible(i)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if a section title should be visible.
   * A section title is visible if at least one of its following menu items is visible.
   */
  isSectionVisible(titleIndex: number): boolean {
    // Look at items after this title until the next title or end of list
    for (let i = titleIndex + 1; i < this.menuItems.length; i++) {
      const nextItem = this.menuItems[i];
      if (nextItem.isTitle) {
        break; // Reached the next section
      }
      if (this.hasMenuAccess(nextItem)) {
        return true; // At least one child is visible
      }
    }
    return false;
  }

  /**
   * Get current language flag for display
   */
  getCurrentFlag(): string {
    const currentLang = this.languageService.getCurrentLanguage();
    const langItem = this.listLang.find(l => l.lang === currentLang);
    return langItem?.flag || 'assets/images/flags/united-kingdom.svg';
  }

  /**
   * Set language (mobile sidebar)
   */
  setLanguage(lang: string): void {
    this.languageService.setLanguage(lang);
  }

  /**
   * Copy the current page's share URL to the clipboard (mobile/tablet sidebar)
   */
  copyShareLink(): void {
    if (!this.managedHotel) {
      this.snackBar.open('Please select a hotel before sharing.', 'OK', { duration: 3000 });
      return;
    }

    const shareUrl = `${window.location.origin}${this.router.url}`;

    navigator.clipboard.writeText(shareUrl).then(() => {
      this.snackBar.open('Share URL copied to clipboard!', 'OK', { duration: 3000, politeness: 'polite' });
    }).catch(err => {
      this.logger.error('Failed to copy URL: ', err);
      this.snackBar.open('Share URL copied to clipboard!', '', { duration: 3000, politeness: 'polite' });
    });
  }

  /**
   * Open the Check HotelCard validity dialog (mobile/tablet sidebar)
   */
  openCardValidityDialog(): void {
    this.dialogService.openCardValidityDialog();
  }

  /**
   * Logout user (mobile sidebar)
   */
  logout(): void {
    this.userService.logout().subscribe({
      next: () => {
        this.router.navigate(['/account/login']);
      },
      error: () => {
        // Even on error, navigate to login
        this.router.navigate(['/account/login']);
      }
    });
  }

  /**
   * Close mobile sidebar (on navigation or link click)
   */
  private closeMobileSidebar(): void {
    if (window.innerWidth < 992 && document.body.classList.contains('sidebar-enable')) {
      document.body.classList.remove('sidebar-enable');
    }
  }
}
